/* =====================================================================
 * DashboardPage — 사용량 대시보드 (개인 스코프).
 *
 * 사이드바 "관리" 섹션의 플러그인 바로 아래에 위치한다. 본인 기록만
 * 보여주므로 관리자 권한 게이트는 없다 — 서버가 토큰의 user_id 로
 * 스코프를 강제하며, 클라이언트가 넓힐 수 있는 파라미터는 없다.
 *
 * 구성(위 → 아래):
 *   1. 진행 상태        지금 실행·대기 중인 작업
 *   2. 잔디 히트맵      1년치 활동 밀도
 *   3. 요약 타일        실행·토큰 총계 / 평균 소요 / 진행 중 / 대기 중
 *   4. 사용량 차트      횟수·토큰 및 일·주·월·날짜 범위 전환
 *   5. 최근 기록        최근 완료된 작업
 * ===================================================================== */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTheme } from 'styled-components';
import {
  Activity,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  Clock,
  Coins,
  RefreshCw,
  Timer,
} from 'lucide-react';
import {
  DashboardUnavailableError,
  fetchDashboardActive,
  fetchDashboardHeatmap,
  fetchDashboardRecent,
  fetchDashboardSeries,
  fetchDashboardSummary,
  type DashboardGranularity,
} from '@auw/api-client';

import { RingSpinner } from '../common/Spinner';
import { RunList } from './RunList';
import { StatTile } from './StatTile';
import { UsageChart } from './UsageChart';
import { UsageHeatmap } from './UsageHeatmap';
import {
  canonicalWorkspaceType,
  dashboardPaginationDisplay,
  formatDashboardTokenCount,
  mergeDocAgentWorkspaces,
  validateDashboardDateRange,
  type UsageMetric,
} from './dashboardMetrics';
import type { ChartMode } from './chartPalette';
import {
  Card,
  CardHead,
  ChartFrame,
  ChartLoading,
  CardHint,
  CardTitle,
  DateField,
  DateInput,
  DateRangeButton,
  DateRangeError,
  DateRangeForm,
  EmptyState,
  PageHeader,
  PageHeaderText,
  PageInner,
  PageScroll,
  PageSubtitle,
  PageTitle,
  PaginationButton,
  PaginationCount,
  PaginationFooter,
  PaginationNav,
  PaginationStatus,
  RefreshButton,
  Segmented,
  SegmentedButton,
  SegmentedControlCluster,
  SegmentedDivider,
  SegmentedGroups,
  StatGrid,
} from './dashboard.styles';

/** Window length per granularity — enough buckets to show a shape,
 *  few enough that the axis stays readable without scrolling on desktop. */
const DAYS_BY_GRANULARITY: Record<DashboardGranularity, number> = {
  day: 30,
  week: 120,
  month: 365,
};

const GRANULARITIES: DashboardGranularity[] = ['day', 'week', 'month'];
const USAGE_METRICS: UsageMetric[] = ['runs', 'tokens'];
const RECENT_PAGE_SIZE = 15;

/**
 * Poll interval for the panels that move while the page is open.
 *
 * 3s rather than 10s: "진행 상태" is the one part of this page a user
 * watches, and a ten-second lag reads as the dashboard being stale
 * rather than the work being slow. Only three small, user-scoped
 * queries are on this interval, and only while the tab is visible —
 * React Query pauses background refetches on a hidden document.
 */
const LIVE_REFETCH_MS = 3_000;

/** Static-ish panels: refreshed on focus and on demand, not on a timer. */
const SLOW_STALE_MS = 60_000;

export const DashboardPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const chartMode: ChartMode = theme.mode === 'dark' ? 'dark' : 'light';
  const [granularity, setGranularity] = useState<DashboardGranularity>('day');
  const [usageMetric, setUsageMetric] = useState<UsageMetric>('runs');
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [dateRangeError, setDateRangeError] = useState<
    'incomplete' | 'invalid' | 'tooLong' | null
  >(null);
  const [appliedDateRange, setAppliedDateRange] = useState<{
    start: string;
    end: string;
  } | null>(null);
  const [recentPage, setRecentPage] = useState(1);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const queryClient = useQueryClient();

  const summary = useQuery({
    queryKey: ['dashboard', 'summary'],
    queryFn: () => fetchDashboardSummary(30),
    refetchInterval: LIVE_REFETCH_MS,
    refetchOnWindowFocus: true,
  });
  const heatmap = useQuery({
    queryKey: ['dashboard', 'heatmap'],
    queryFn: () => fetchDashboardHeatmap(365),
    staleTime: SLOW_STALE_MS,
  });
  const series = useQuery({
    queryKey: [
      'dashboard',
      'series',
      granularity,
      appliedDateRange?.start ?? 'preset',
      appliedDateRange?.end ?? DAYS_BY_GRANULARITY[granularity],
    ],
    queryFn: () =>
      fetchDashboardSeries(
        granularity,
        DAYS_BY_GRANULARITY[granularity],
        appliedDateRange ?? undefined,
      ),
    staleTime: SLOW_STALE_MS,
    /* 기간을 바꾸는 동안 이전 그래프를 흐리게 남겨 둔다(ChartFrame). */
    placeholderData: (previousData) => previousData,
  });
  const active = useQuery({
    queryKey: ['dashboard', 'active'],
    queryFn: fetchDashboardActive,
    refetchInterval: LIVE_REFETCH_MS,
    refetchOnWindowFocus: true,
  });
  const recent = useQuery({
    queryKey: ['dashboard', 'recent', recentPage],
    queryFn: () => fetchDashboardRecent(
      RECENT_PAGE_SIZE,
      (recentPage - 1) * RECENT_PAGE_SIZE,
    ),
    placeholderData: (previousData) => previousData,
    refetchInterval: recentPage === 1 ? LIVE_REFETCH_MS : false,
    // Older offset pages stay fixed while being read. Page 1 remains live.
    refetchOnWindowFocus: recentPage === 1,
  });

  // Background polling must not animate the header control. Its busy state
  // represents only a refresh explicitly requested by the user.
  const refreshAll = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await queryClient.invalidateQueries({
        queryKey: ['dashboard'],
        refetchType: 'active',
      });
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, queryClient]);

  const applyDateRange = useCallback((event: React.FormEvent) => {
    event.preventDefault();
    const error = validateDashboardDateRange(dateStart, dateEnd);
    setDateRangeError(error);
    if (error) return;
    setAppliedDateRange({ start: dateStart, end: dateEnd });
  }, [dateEnd, dateStart]);

  const clearDateRange = useCallback(() => {
    setDateStart('');
    setDateEnd('');
    setDateRangeError(null);
    setAppliedDateRange(null);
  }, []);

  /** Workspace id → human label, falling back to the raw id so a newly
   *  added agent type shows up rather than disappearing. */
  const labelFor = useCallback(
    (workspaceType: string) => {
      const key = `common:dashboard.workspace.${canonicalWorkspaceType(
        workspaceType,
      )}`;
      const label = t(key);
      return label === key ? workspaceType : label;
    },
    [t],
  );

  /* 문서 에이전트 포맷별 기록을 한 항목으로 접은 차트용 시리즈. */
  const chartSeries = useMemo(
    () => (series.data ? mergeDocAgentWorkspaces(series.data) : undefined),
    [series.data],
  );

  const unavailable = useMemo(
    () =>
      [summary.error, heatmap.error, series.error].some(
        (error) => error instanceof DashboardUnavailableError,
      ),
    [summary.error, heatmap.error, series.error],
  );

  const avgSeconds = summary.data
    ? Math.round(summary.data.avg_duration_ms / 100) / 10
    : 0;
  const knownTokenRuns = summary.data?.token_run_count ?? 0;
  const unknownTokenRuns = summary.data?.unknown_token_run_count ?? 0;
  const tokenRunTotal = knownTokenRuns + unknownTokenRuns;
  const tokenCoverageHint = unknownTokenRuns > 0
    ? t('common:dashboard.tokenCoverage', {
        known: knownTokenRuns,
        total: tokenRunTotal,
      })
    : undefined;
  const recentTotal = recent.data?.total ?? 0;
  // While the next page is loading React Query deliberately keeps the
  // previous rows. Derive the visible range from that response's offset so
  // the footer never labels page-1 rows as page 2.
  const pagination = dashboardPaginationDisplay(
    recentTotal,
    RECENT_PAGE_SIZE,
    recentPage,
    recent.data?.offset,
    recent.data?.runs.length ?? 0,
  );
  const recentTotalPages = pagination.totalPages;

  useEffect(() => {
    if (recentPage > recentTotalPages) setRecentPage(recentTotalPages);
  }, [recentPage, recentTotalPages]);

  return (
    <PageScroll>
      <PageInner>
        <PageHeader>
          <PageHeaderText>
            <PageTitle>{t('common:dashboard.title')}</PageTitle>
            <PageSubtitle>{t('common:dashboard.subtitle')}</PageSubtitle>
          </PageHeaderText>
          <RefreshButton
            type="button"
            onClick={() => void refreshAll()}
            disabled={isRefreshing}
            $busy={isRefreshing}
            aria-label={t('common:dashboard.refresh')}
            title={t('common:dashboard.refresh')}
          >
            <RefreshCw aria-hidden />
          </RefreshButton>
        </PageHeader>

        {unavailable ? (
          <Card>
            <EmptyState>{t('common:dashboard.unavailable')}</EmptyState>
          </Card>
        ) : (
          <>
            {/* 1 — 진행 상태 */}
            <Card>
              <CardHead>
                <CardTitle>
                  <Activity aria-hidden size={16} />
                  {t('common:dashboard.activeTitle')}
                </CardTitle>
                <CardHint>{t('common:dashboard.activeHint')}</CardHint>
              </CardHead>
              <RunList
                runs={active.data?.runs ?? []}
                mode="active"
                labelFor={labelFor}
                emptyText={t('common:dashboard.noActive')}
              />
            </Card>

            {/* 2 — 잔디 */}
            <Card>
              <CardHead>
                <CardTitle>
                  <Activity aria-hidden size={16} />
                  {t('common:dashboard.activityTitle')}
                </CardTitle>
                {heatmap.data && (
                  <CardHint>
                    {t('common:dashboard.activityTotal', {
                      count: heatmap.data.total,
                    })}
                  </CardHint>
                )}
              </CardHead>
              {heatmap.data ? (
                <UsageHeatmap data={heatmap.data} mode={chartMode} />
              ) : (
                <EmptyState>
                  {heatmap.isLoading
                    ? t('common:dashboard.loading')
                    : t('common:dashboard.noUsageYet')}
                </EmptyState>
              )}
            </Card>

            {/* 3 — 요약 */}
            <StatGrid>
              <StatTile
                icon={BarChart3}
                label={t('common:dashboard.statTotal')}
                value={summary.data?.total_runs ?? '—'}
                unit={t('common:dashboard.unitRuns')}
              />
              <StatTile
                icon={Coins}
                label={t(
                  unknownTokenRuns > 0
                    ? 'common:dashboard.statCollectedTokens'
                    : 'common:dashboard.statTotalTokens',
                )}
                value={summary.data
                  ? formatDashboardTokenCount(summary.data.total_token_count)
                  : '—'}
                unit={t('common:dashboard.unitTokens')}
                hint={tokenCoverageHint}
                title={summary.data?.total_token_count == null
                  ? tokenCoverageHint
                  : `${summary.data.total_token_count.toLocaleString(
                      i18n.language,
                    )} ${t('common:dashboard.unitTokens')}${
                      tokenCoverageHint ? ` · ${tokenCoverageHint}` : ''
                    }`}
              />
              <StatTile
                icon={Timer}
                label={t('common:dashboard.statAvgDuration')}
                value={summary.data ? avgSeconds : '—'}
                unit={t('common:dashboard.unitSeconds')}
              />
              <StatTile
                icon={Activity}
                label={t('common:dashboard.statRunning')}
                value={summary.data?.running_count ?? '—'}
              />
              <StatTile
                icon={Clock}
                label={t('common:dashboard.statQueued')}
                value={summary.data?.queued_count ?? '—'}
              />
            </StatGrid>

            {/* 4 — 사용량 차트 */}
            <Card>
              <CardHead>
                <CardTitle>
                  <BarChart3 aria-hidden size={16} />
                  {t('common:dashboard.usageTitle')}
                </CardTitle>
                <SegmentedGroups>
                  {usageMetric === 'tokens'
                    && (series.data?.unknown_token_run_count ?? 0) > 0 && (
                    <CardHint>
                      {t('common:dashboard.tokenCoverage', {
                        known: series.data?.token_run_count ?? 0,
                        total: (series.data?.token_run_count ?? 0)
                          + (series.data?.unknown_token_run_count ?? 0),
                      })}
                    </CardHint>
                  )}
                  <SegmentedControlCluster>
                    <Segmented
                      role="group"
                      aria-label={t('common:dashboard.metricTabLabel')}
                    >
                      {USAGE_METRICS.map((metric) => (
                        <SegmentedButton
                          key={metric}
                          type="button"
                          aria-pressed={usageMetric === metric}
                          $active={usageMetric === metric}
                          onClick={() => setUsageMetric(metric)}
                        >
                          {t(`common:dashboard.usageMetric.${metric}`)}
                        </SegmentedButton>
                      ))}
                    </Segmented>
                    <SegmentedDivider aria-hidden="true" />
                    <Segmented
                      role="group"
                      aria-label={t('common:dashboard.granularityTabLabel')}
                    >
                      {GRANULARITIES.map((option) => (
                        <SegmentedButton
                          key={option}
                          type="button"
                          aria-pressed={granularity === option}
                          $active={granularity === option}
                          onClick={() => setGranularity(option)}
                        >
                          {t(`common:dashboard.granularity.${option}`)}
                        </SegmentedButton>
                      ))}
                    </Segmented>
                  </SegmentedControlCluster>
                </SegmentedGroups>
              </CardHead>
              <DateRangeForm
                onSubmit={applyDateRange}
                aria-label={t('common:dashboard.dateRangeLabel')}
              >
                <DateField>
                  {t('common:dashboard.dateStart')}
                  <DateInput
                    type="date"
                    value={dateStart}
                    aria-invalid={dateRangeError != null}
                    onChange={(event) => {
                      setDateStart(event.target.value);
                      setDateRangeError(null);
                    }}
                  />
                </DateField>
                <DateField>
                  {t('common:dashboard.dateEnd')}
                  <DateInput
                    type="date"
                    value={dateEnd}
                    aria-invalid={dateRangeError != null}
                    onChange={(event) => {
                      setDateEnd(event.target.value);
                      setDateRangeError(null);
                    }}
                  />
                </DateField>
                <DateRangeButton type="submit" $primary>
                  {t('common:dashboard.applyDateRange')}
                </DateRangeButton>
                {(dateStart || dateEnd || appliedDateRange) && (
                  <DateRangeButton type="button" onClick={clearDateRange}>
                    {t('common:dashboard.clearDateRange')}
                  </DateRangeButton>
                )}
                {dateRangeError && (
                  <DateRangeError role="alert">
                    {t(`common:dashboard.dateRangeError.${dateRangeError}`)}
                  </DateRangeError>
                )}
              </DateRangeForm>
              {chartSeries ? (
                <ChartFrame $loading={series.isPlaceholderData} aria-busy={series.isPlaceholderData}>
                  <UsageChart
                    data={chartSeries}
                    metric={usageMetric}
                    mode={chartMode}
                    labelFor={labelFor}
                  />
                  {series.isPlaceholderData && (
                    <ChartLoading data-chart-loading role="status">
                      <RingSpinner size={14} />
                      {t('common:dashboard.loading')}
                    </ChartLoading>
                  )}
                </ChartFrame>
              ) : (
                <EmptyState>
                  {series.isLoading
                    ? t('common:dashboard.loading')
                    : t('common:dashboard.noUsageYet')}
                </EmptyState>
              )}
            </Card>

            {/* 5 — 최근 기록 */}
            <Card>
              <CardHead>
                <CardTitle>
                  <Clock aria-hidden size={16} />
                  {t('common:dashboard.recentTitle')}
                </CardTitle>
              </CardHead>
              <RunList
                runs={recent.data?.runs ?? []}
                mode="recent"
                labelFor={labelFor}
                emptyText={t('common:dashboard.noRecent')}
              />
              {recentTotal > 0 && (
                <PaginationFooter>
                  <PaginationCount>
                    {t('common:dashboard.recentCount', {
                      start: pagination.start,
                      end: pagination.end,
                      total: recentTotal,
                    })}
                  </PaginationCount>
                  <PaginationNav
                    aria-label={t('common:dashboard.recentPagination')}
                  >
                    <PaginationButton
                      type="button"
                      disabled={recentPage <= 1 || recent.isPlaceholderData}
                      onClick={() => setRecentPage((page) => Math.max(1, page - 1))}
                      aria-label={t('common:dashboard.previousPage')}
                    >
                      <ChevronLeft aria-hidden size={16} />
                    </PaginationButton>
                    <PaginationStatus>
                      {t('common:dashboard.pageStatus', {
                        page: pagination.displayedPage,
                        total: recentTotalPages,
                      })}
                    </PaginationStatus>
                    <PaginationButton
                      type="button"
                      disabled={
                        recentPage >= recentTotalPages
                        || recent.isPlaceholderData
                      }
                      onClick={() => setRecentPage((page) => (
                        Math.min(recentTotalPages, page + 1)
                      ))}
                      aria-label={t('common:dashboard.nextPage')}
                    >
                      <ChevronRight aria-hidden size={16} />
                    </PaginationButton>
                  </PaginationNav>
                </PaginationFooter>
              )}
            </Card>
          </>
        )}
      </PageInner>
    </PageScroll>
  );
};
